import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { getSaleById, getCustomerById, getSaleItems, getInstallmentsBySaleId } from '@/lib/supabaseDb';
import { formatTRY } from '@/lib/currencyFormat';

const formatReceipt = (amount: number): string => formatTRY(amount, { minFractionDigits: 2 });

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

function todayTR(): string {
  const d = new Date();
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function numberToWordsTL(amount: number): string {
  const ones = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
  const tens = ['', 'On', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];

  if (amount === 0) return 'Sıfır';
  const lira = Math.floor(amount);
  const kurus = Math.round((amount - lira) * 100);

  function convertHundreds(n: number): string {
    if (n === 0) return '';
    let result = '';
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    if (hundred > 0) result += hundred === 1 ? 'Yüz' : ones[hundred] + 'Yüz';
    const ten = Math.floor(rest / 10);
    const one = rest % 10;
    if (ten > 0) result += tens[ten];
    if (one > 0) result += ones[one];
    return result;
  }

  function convertGroup(n: number): string {
    if (n === 0) return '';
    let result = '';
    const milyon = Math.floor(n / 1000000);
    const bin = Math.floor((n % 1000000) / 1000);
    const yuz = n % 1000;
    if (milyon > 0) result += convertHundreds(milyon) + 'Milyon';
    if (bin > 0) result += (bin === 1 && milyon === 0 ? '' : convertHundreds(bin)) + 'Bin';
    if (yuz > 0) result += convertHundreds(yuz);
    return result;
  }

  let text = convertGroup(lira) + 'Türk Lirası';
  if (kurus > 0) text += ' ve ' + convertGroup(kurus) + 'Kuruş';
  return text;
}

function ReceiptContent({ sale, customer, items, installments }: {
  sale: any;
  customer: any;
  items: any[];
  installments: any[];
}) {
  const saleDateFormatted = sale.sale_date ? formatDate(sale.sale_date) : todayTR();

  return (
    <>
      <div style={{ borderBottom: '3px double #1a1a1a', paddingBottom: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 800, margin: '0 0 2px 0', letterSpacing: '-0.5px' }}>POLATLAR MOBİLYA</h1>
            <p style={{ fontSize: '14px', margin: '0 0 2px 0', color: '#444' }}>Mobilya ve Profilo Bayi</p>
            <p style={{ fontSize: '11px', margin: '0', color: '#666' }}>Satış Makbuzu / Fatura Yerine Geçer</p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '12px', color: '#555' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>SATIŞ MAKBUZU</p>
            <p style={{ margin: '0 0 4px 0' }}><strong>Tarih:</strong> {saleDateFormatted}</p>
            <p style={{ margin: '0 0 4px 0' }}><strong>Makbuz No:</strong> {sale.sale_no}</p>
            <p style={{ margin: '0' }}><strong>Sayfa:</strong> 1 / 1</p>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase', letterSpacing: '1px' }}>Müşteri Bilgileri</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            <tr><td style={{ padding: '4px 8px', width: '25%', fontWeight: 500, color: '#555' }}>Ad Soyad:</td><td style={{ padding: '4px 8px', width: '25%', fontWeight: 600 }}>{customer?.full_name || sale.customer_name || '—'}</td><td style={{ padding: '4px 8px', width: '25%', fontWeight: 500, color: '#555' }}>Müşteri No:</td><td style={{ padding: '4px 8px', width: '25%' }}>{customer?.customer_no || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Telefon:</td><td style={{ padding: '4px 8px' }}>{customer?.phone || sale.customer_phone || '—'}</td><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>2. Telefon:</td><td style={{ padding: '4px 8px' }}>{customer?.second_phone || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Adres:</td><td style={{ padding: '4px 8px' }} colSpan={3}>{customer?.address || sale.delivery_address || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>İlçe:</td><td style={{ padding: '4px 8px' }}>{customer?.district || '—'}</td><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Ödeme Tipi:</td><td style={{ padding: '4px 8px' }}>{sale.payment_type}{sale.installment_count > 0 ? ` (${sale.installment_count} Taksit)` : ''}</td></tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase', letterSpacing: '1px' }}>Satılan Ürünler</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #1a1a1a', backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, width: '30px' }}>Sıra</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Ürün Cinsi</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Marka</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Ürün Adı</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Model Kodu</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Renk</th>
              <th style={{ padding: '6px 6px', textAlign: 'left', fontWeight: 600 }}>Ölçü</th>
              <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, width: '40px' }}>Adet</th>
              <th style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 600, width: '80px' }}>Birim Fiyat</th>
              <th style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 600, width: '90px' }}>Tutar</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? items.map((item: any, idx: number) => (
              <tr key={item.id || idx} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '5px 6px', textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ padding: '5px 6px' }}>{item.product_type || item.category || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.brand || '—'}</td>
                <td style={{ padding: '5px 6px', fontWeight: 500 }}>{item.product_name || '—'}</td>
                <td style={{ padding: '5px 6px', fontSize: '10px' }}>{item.model_code || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.color || '—'}</td>
                <td style={{ padding: '5px 6px' }}>{item.size || '—'}</td>
                <td style={{ padding: '5px 6px', textAlign: 'center' }}>{item.quantity || 1}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatReceipt(item.unit_price)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>{formatReceipt(item.total_price)}</td>
              </tr>
            )) : (
              <tr><td colSpan={10} style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', color: '#999' }}>Ürün bilgisi bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <table style={{ width: '320px', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #ddd' }}><td style={{ padding: '6px 12px', fontWeight: 500, color: '#555' }}>Ara Toplam:</td><td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 500 }}>{formatReceipt(sale.total_amount + (sale.discount_amount || 0))}</td></tr>
            {sale.discount_amount > 0 && (<tr style={{ borderBottom: '1px solid #ddd' }}><td style={{ padding: '6px 12px', fontWeight: 500, color: '#c0392b' }}>İndirim:</td><td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 500, color: '#c0392b' }}>-{formatReceipt(sale.discount_amount)}</td></tr>)}
            <tr style={{ borderBottom: '2px solid #1a1a1a' }}><td style={{ padding: '8px 12px', fontWeight: 700, fontSize: '14px' }}>TOPLAM:</td><td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: '14px' }}>{formatReceipt(sale.total_amount)}</td></tr>
            <tr style={{ borderBottom: '1px solid #ddd' }}><td style={{ padding: '6px 12px', fontWeight: 500, color: '#555' }}>Peşinat:</td><td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 500 }}>{formatReceipt(sale.down_payment)}</td></tr>
            <tr style={{ borderBottom: '1px solid #ddd' }}><td style={{ padding: '6px 12px', fontWeight: 500, color: '#555' }}>Ödenen:</td><td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 500, color: '#27ae60' }}>{formatReceipt(sale.paid_amount)}</td></tr>
            {sale.remaining_amount > 0 && (<tr><td style={{ padding: '6px 12px', fontWeight: 600, color: '#c0392b' }}>Kalan Borç:</td><td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: '#c0392b' }}>{formatReceipt(sale.remaining_amount)}</td></tr>)}
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: '20px', padding: '10px 14px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#fafafa' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#555' }}>Yalnız: </span>
        <span style={{ fontSize: '12px', fontStyle: 'italic' }}>{numberToWordsTL(sale.total_amount)}</span>
      </div>

      {installments.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase', letterSpacing: '1px' }}>Taksit Planı{installments[0]?.plan_type === 'custom' ? ' (Özel Plan)' : ''}</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #1a1a1a', backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600, width: '30px' }}>#</th>
                <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600 }}>Vade Tarihi</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>Ödenen</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>Kalan</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600 }}>Durum</th>
                <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600 }}>Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {installments.sort((a: any, b: any) => (a.sequence_no || 0) - (b.sequence_no || 0)).map((inst: any) => {
                const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : ((inst.amount || 0) - (inst.paid_amount || 0));
                return (
                <tr key={inst.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>{inst.installment_no || inst.sequence_no || '?'}</td>
                  <td style={{ padding: '4px 8px' }}>{inst.due_date ? new Date(inst.due_date).toLocaleDateString('tr-TR') : '—'}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 500 }}>{formatReceipt(inst.original_amount || inst.amount)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{inst.paid_amount > 0 ? formatReceipt(inst.paid_amount) : '—'}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{instRemaining > 0 ? formatReceipt(instRemaining) : '—'}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, backgroundColor: inst.status === 'Ödendi' ? '#d4edda' : inst.status === 'Kısmi Ödendi' ? '#fff3cd' : inst.status === 'Gecikti' ? '#f8d7da' : '#e2e3e5', color: inst.status === 'Ödendi' ? '#155724' : inst.status === 'Kısmi Ödendi' ? '#856404' : inst.status === 'Gecikti' ? '#721c24' : '#383d41' }}>{inst.status}</span>
                  </td>
                  <td style={{ padding: '4px 8px', fontSize: '10px', color: '#666' }}>{inst.description || inst.notes || '—'}</td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #1a1a1a', textTransform: 'uppercase', letterSpacing: '1px' }}>Ödeme ve Garanti Bilgileri</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            <tr><td style={{ padding: '4px 8px', width: '25%', fontWeight: 500, color: '#555' }}>Ödeme Tipi:</td><td style={{ padding: '4px 8px', width: '25%' }}>{sale.payment_type}</td><td style={{ padding: '4px 8px', width: '25%', fontWeight: 500, color: '#555' }}>Garanti Süresi:</td><td style={{ padding: '4px 8px', width: '25%' }}>{sale.warranty_period || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Teslimat Durumu:</td><td style={{ padding: '4px 8px' }}>{sale.delivery_status || '—'}</td><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Personel:</td><td style={{ padding: '4px 8px' }}>{sale.created_by || '—'}</td></tr>
            <tr><td style={{ padding: '4px 8px', fontWeight: 500, color: '#555' }}>Teslimat Adresi:</td><td style={{ padding: '4px 8px' }} colSpan={3}>{sale.delivery_address || '—'}</td></tr>
          </tbody>
        </table>
      </div>

      {sale.notes && (
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 6px 0', paddingBottom: '4px', borderBottom: '1px solid #999' }}>Notlar</h2>
          <p style={{ fontSize: '12px', margin: '0', padding: '8px', backgroundColor: '#f9f9f9', border: '1px solid #eee', borderRadius: '4px', minHeight: '30px' }}>{sale.notes}</p>
        </div>
      )}

      <div style={{ marginTop: '30px', display: 'flex', gap: '60px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ height: '60px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: '#999' }}>Satıcı İmza</span>
          </div>
          <p style={{ fontSize: '11px', margin: '6px 0 0 0', color: '#555' }}>Polatlar Mobilya</p>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ height: '60px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: '#999' }}>Müşteri İmza</span>
          </div>
          <p style={{ fontSize: '11px', margin: '6px 0 0 0', color: '#555' }}>{customer?.full_name || sale.customer_name || ''}</p>
        </div>
      </div>

      <div style={{ marginTop: '30px', paddingTop: '12px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
        <p style={{ fontSize: '10px', color: '#999', margin: '0 0 2px 0' }}>Bu makbuz, yukarıda belirtilen satış işleminin yapıldığını teyit eder.</p>
        <p style={{ fontSize: '10px', color: '#aaa', margin: '0' }}>Polatlar Mobilya — {sale.sale_no} — {saleDateFormatted} — Sayfa 1/1</p>
      </div>
    </>
  );
}

export default function ReceiptPage() {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [sale, setSale] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [installments, setInstallments] = useState<any[]>([]);

  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const div = document.createElement('div');
    div.id = 'receipt-print-root';
    div.style.display = 'none';
    document.body.appendChild(div);
    portalContainerRef.current = div;

    return () => {
      if (div && document.body.contains(div)) {
        document.body.removeChild(div);
      }
    };
  }, []);

  useEffect(() => {
    if (!saleId) return;
    async function load() {
      setLoading(true);
      const [saleRes, itemsRes, instRes] = await Promise.all([
        getSaleById(saleId!),
        getSaleItems(saleId!),
        getInstallmentsBySaleId(saleId!),
      ]);
      const saleData = saleRes.data;
      setSale(saleData);
      setItems(itemsRes.data || []);
      setInstallments(instRes.data || []);

      if (saleData?.customer_id) {
        const custRes = await getCustomerById(saleData.customer_id);
        setCustomer(custRes.data);
      }
      setLoading(false);
    }
    load();
  }, [saleId]);

  const handlePrint = useCallback(() => {
    window.dispatchEvent(new CustomEvent('receipt-before-print'));
    setTimeout(() => {
      window.print();
    }, 150);
  }, []);

  const handlePdfSave = useCallback(() => {
    setShowToast('PDF olarak kaydetmek için yazdırma penceresinde "PDF olarak kaydet" seçeneğini kullanın.');
    setTimeout(() => setShowToast(null), 4000);
    window.dispatchEvent(new CustomEvent('receipt-before-print'));
    setTimeout(() => {
      window.print();
    }, 300);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
        </div>
        <h2 className="text-lg font-semibold text-foreground-700">Satış bulunamadı</h2>
        <button onClick={() => navigate('/sales')} className="btn-primary mt-4 cursor-pointer">Listeye Dön</button>
      </div>
    );
  }

  const receiptPortal = portalContainerRef.current
    ? createPortal(
        <div id="receipt-print-area" className="receipt-report" style={{ maxWidth: '190mm', width: '100%', margin: '0 auto', padding: '15mm 15mm', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px', lineHeight: '1.5', color: '#1a1a1a', background: 'white' }}>
          <ReceiptContent sale={sale} customer={customer} items={items} installments={installments} />
        </div>,
        portalContainerRef.current
      )
    : null;

  return (
    <div className="space-y-4 max-w-[1000px] mx-auto">
      {receiptPortal}

      {showToast && (
        <div className="fixed top-4 right-4 z-[300] bg-accent-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2">
          <i className="ri-information-line"></i>
          <span className="text-sm font-medium">{showToast}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 no-print">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/sales/${sale.id}`)} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-foreground-600"></i>
          </button>
          <div>
            <h2 className="text-lg font-bold text-foreground-900 font-heading">Satış Makbuzu</h2>
            <p className="text-xs text-foreground-400">{sale.sale_no} — Yazdırılabilir A4</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <button onClick={handlePrint} className="no-print flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-printer-line"></i>Yazdır
          </button>
          <button onClick={handlePdfSave} className="no-print flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-file-pdf-2-line"></i>PDF Kaydet
          </button>
        </div>
      </div>

      <div className="bg-background-100 rounded-xl p-4 flex justify-center receipt-outer no-print">
        <div className="receipt-report bg-white shadow-sm" style={{ maxWidth: '210mm', minHeight: '297mm', width: '100%', padding: '18mm 20mm', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '13px', lineHeight: '1.5', color: '#1a1a1a' }}>
          <ReceiptContent sale={sale} customer={customer} items={items} installments={installments} />
        </div>
      </div>
    </div>
  );
}